import React from 'react';
import { formatCurrencyShort, formatDelta } from '../../../../utils/formatters';
import modalStyles from '../../../../styles/modals.module.css';

/** Describe the option change for a single item in the summary list. */
const describeChange = change => {
  const parts = [];

  // Option label change
  const oldLabel = getOptionLabel(change.oldOption);
  const newLabel = getOptionLabel(change.newOption);
  if (oldLabel !== newLabel) {
    parts.push(`${oldLabel || 'ללא'} → ${newLabel || 'ללא'}`);
  }

  // Cost change
  if (change.oldCost !== change.newCost) {
    const oldStr = change.oldCost != null ? formatCurrencyShort(change.oldCost) : 'ללא';
    const newStr = change.newCost != null ? formatCurrencyShort(change.newCost) : 'ללא';
    parts.push(`${oldStr} → ${newStr}`);
  }

  return parts.join(' · ') || 'שינוי';
};

/** Extract a human-readable label from a selected_option object. */
const getOptionLabel = opt => {
  if (!opt) return null;
  // For choice_bundle
  if (opt.selected_child_id) return opt.selected_child_id;
  // For single_group
  if (opt.default) return opt.default;
  // For multi_group — join group values, skip metadata keys and non-string values
  const skipKeys = new Set(['cost', 'notes', 'costManuallySet']);
  const vals = Object.entries(opt)
    .filter(([key, val]) => !skipKeys.has(key) && val && typeof val === 'string')
    .map(([, val]) => val);
  return vals.length > 0 ? vals.join(', ') : null;
};

const SimulationSummaryModal = React.memo(function SimulationSummaryModal({
  changes,
  totalDelta,
  onConfirm,
  onCancel,
  isApplying,
}) {
  return (
    <div className={modalStyles.overlay} onClick={onCancel}>
      <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>סיכום שינויים</h2>
          <p className={modalStyles.headerSubtitle}>{changes.length} שינויים לפני שמירה</p>
        </div>

        <div className={modalStyles.content}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {changes.map(change => (
              <div
                key={change.masterItemId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  gap: '12px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                    {change.itemName}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>
                    {describeChange(change)}
                    <span style={{ marginRight: '8px', color: '#94a3b8' }}>
                      ({change.itemCount} פריטים)
                    </span>
                  </div>
                  {change.quotedCount > 0 && (
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                      {change.quotedCount} פריטים עם הצעת מחיר פעילה לא ישתנו
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color:
                      change.costDelta > 0
                        ? '#dc2626'
                        : change.costDelta < 0
                          ? '#16a34a'
                          : '#64748b',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatDelta(change.costDelta)}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              marginTop: '16px',
              background: totalDelta > 0 ? '#fef2f2' : totalDelta < 0 ? '#f0fdf4' : '#f8fafc',
              borderRadius: '8px',
              border: `1px solid ${totalDelta > 0 ? '#fecaca' : totalDelta < 0 ? '#bbf7d0' : '#e2e8f0'}`,
            }}
          >
            <span style={{ fontWeight: 600, color: '#1e293b' }}>סה״כ השפעה</span>
            <span
              style={{
                fontWeight: 700,
                fontSize: '18px',
                color: totalDelta > 0 ? '#dc2626' : totalDelta < 0 ? '#16a34a' : '#64748b',
              }}
            >
              {formatDelta(totalDelta)}
            </span>
          </div>
        </div>

        <div className={modalStyles.footer}>
          <button className={modalStyles.cancelButton} onClick={onCancel} type="button">
            חזרה
          </button>
          <button
            className={modalStyles.primaryButton}
            onClick={onConfirm}
            disabled={isApplying}
            type="button"
          >
            {isApplying ? 'שומר...' : 'אישור ושמירה'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default SimulationSummaryModal;
